import type { AgentId } from "./nav.js";
import { AGENTS } from "./nav.js";

export type FirmKitChannel = "email" | "linkedin" | "phone";

export type FirmKitChannelStatus =
	| "planned"
	| "workspace_ready"
	| "oauth_pending"
	| "connected"
	| "error";

export type FirmKitChannelRow = {
	channel: FirmKitChannel;
	/** Public address / handle / E.164 when known. */
	identity: string;
	status: FirmKitChannelStatus;
	note: string;
};

export type FirmKitRow = {
	agentId: AgentId;
	channels: FirmKitChannelRow[];
};

const KIT_KEY = "mairko-admin-firm-kit";

/** Firm employee identities — Mairko domain, not the client’s. */
const DEFAULT_KIT: Record<
	AgentId,
	Omit<FirmKitChannelRow, "status" | "note">[]
> = {
	onboarding: [
		{ channel: "email", identity: "jules@mairko.ai" },
		{ channel: "linkedin", identity: "Jules · Onboarding at Mairko" },
		{ channel: "phone", identity: "" },
	],
	paid_media: [
		{ channel: "email", identity: "miles@mairko.ai" },
		{ channel: "linkedin", identity: "Miles · Paid Ads at Mairko" },
		{ channel: "phone", identity: "" },
	],
	ga4_anomaly: [
		{ channel: "email", identity: "nadia@mairko.ai" },
		{ channel: "linkedin", identity: "Nadia · Analytics at Mairko" },
		{ channel: "phone", identity: "" },
	],
	seo: [
		{ channel: "email", identity: "theo@mairko.ai" },
		{ channel: "linkedin", identity: "Theo · SEO at Mairko" },
		{ channel: "phone", identity: "" },
	],
	content: [
		{ channel: "email", identity: "iris@mairko.ai" },
		{ channel: "linkedin", identity: "Iris · Content at Mairko" },
		{ channel: "phone", identity: "" },
	],
	social: [
		{ channel: "email", identity: "sol@mairko.ai" },
		{ channel: "linkedin", identity: "Sol · Social at Mairko" },
		{ channel: "phone", identity: "" },
	],
	email: [
		{ channel: "email", identity: "vera@mairko.ai" },
		{ channel: "linkedin", identity: "Vera · Email at Mairko" },
		{ channel: "phone", identity: "" },
	],
	creative: [
		{ channel: "email", identity: "kai@mairko.ai" },
		{ channel: "linkedin", identity: "Kai · Creative at Mairko" },
		{ channel: "phone", identity: "" },
	],
	cro: [
		{ channel: "email", identity: "quinn@mairko.ai" },
		{ channel: "linkedin", identity: "Quinn · CRO at Mairko" },
		{ channel: "phone", identity: "" },
	],
	local: [
		{ channel: "email", identity: "dana@mairko.ai" },
		{ channel: "linkedin", identity: "Dana · Local at Mairko" },
		{ channel: "phone", identity: "" },
	],
	developer: [
		{ channel: "email", identity: "rhea@mairko.ai" },
		{ channel: "linkedin", identity: "Rhea · Developer at Mairko" },
		{ channel: "phone", identity: "" },
	],
	reporting: [
		{ channel: "email", identity: "arlo@mairko.ai" },
		{ channel: "linkedin", identity: "Arlo · Reporting at Mairko" },
		{ channel: "phone", identity: "" },
	],
};

type StoredKit = Partial<
	Record<
		AgentId,
		Partial<
			Record<FirmKitChannel, { status: FirmKitChannelStatus; note: string }>
		>
	>
>;

function readStored(): StoredKit {
	try {
		const raw = localStorage.getItem(KIT_KEY);
		if (!raw) return {};
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return {};
		return parsed as StoredKit;
	} catch {
		return {};
	}
}

function writeStored(next: StoredKit): void {
	try {
		localStorage.setItem(KIT_KEY, JSON.stringify(next));
	} catch {
		/* ignore */
	}
}

export function loadFirmKit(): FirmKitRow[] {
	const stored = readStored();
	return AGENTS.map((agent) => {
		const defaults = DEFAULT_KIT[agent.id] ?? [];
		const channels: FirmKitChannelRow[] = defaults.map((entry) => {
			const override = stored[agent.id]?.[entry.channel];
			return {
				...entry,
				status: override?.status ?? "planned",
				note: override?.note ?? "",
			};
		});
		return { agentId: agent.id, channels };
	});
}

export function updateFirmKitChannel(
	agentId: AgentId,
	channel: FirmKitChannel,
	patch: { status?: FirmKitChannelStatus; note?: string },
): FirmKitRow[] {
	const stored = readStored();
	const prev = stored[agentId]?.[channel] ?? {
		status: "planned" as FirmKitChannelStatus,
		note: "",
	};
	stored[agentId] = {
		...stored[agentId],
		[channel]: {
			status: patch.status ?? prev.status,
			note: patch.note ?? prev.note,
		},
	};
	writeStored(stored);
	return loadFirmKit();
}

export function firmKitStatusLabel(status: FirmKitChannelStatus): string {
	switch (status) {
		case "planned":
			return "Planned";
		case "workspace_ready":
			return "Workspace ready";
		case "oauth_pending":
			return "OAuth pending";
		case "connected":
			return "Connected";
		case "error":
			return "Error";
		default: {
			const _exhaustive: never = status;
			return _exhaustive;
		}
	}
}

export function firmKitSummary(rows: FirmKitRow[]): {
	emailReady: number;
	linkedinReady: number;
	phoneReady: number;
	totalAgents: number;
} {
	let emailReady = 0;
	let linkedinReady = 0;
	let phoneReady = 0;
	for (const row of rows) {
		for (const channel of row.channels) {
			const ready =
				channel.status === "connected" ||
				channel.status === "workspace_ready";
			if (!ready) continue;
			switch (channel.channel) {
				case "email":
					emailReady += 1;
					break;
				case "linkedin":
					linkedinReady += 1;
					break;
				case "phone":
					phoneReady += 1;
					break;
				default: {
					const _exhaustive: never = channel.channel;
					void _exhaustive;
				}
			}
		}
	}
	return {
		emailReady,
		linkedinReady,
		phoneReady,
		totalAgents: rows.length,
	};
}
